import { useState, type InputHTMLAttributes } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export function Input({ label, error, hint, id, className = '', type, ...rest }: InputProps) {
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === 'password';

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className={isPassword ? 'relative' : undefined}>
        <input
          id={id}
          type={isPassword ? (showPw ? 'text' : 'password') : type}
          className={`rounded-lg border px-3 py-2 text-sm text-gray-900 shadow-sm outline-none
            placeholder:text-gray-400
            focus:border-primary-600 focus:ring-1 focus:ring-primary-700
            disabled:bg-gray-50 disabled:text-gray-500
            ${error ? 'border-red-400' : 'border-gray-300'}
            ${isPassword ? 'pr-16' : ''}
            ${className}`}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPw((s) => !s)}
            tabIndex={-1}
            aria-label={showPw ? 'Hide password' : 'Show password'}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500 hover:text-gray-700"
          >
            {showPw ? 'Hide' : 'Show'}
          </button>
        )}
      </div>
      {hint && !error && <p className="text-xs text-gray-500">{hint}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

interface SelectProps extends InputHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  children: React.ReactNode;
}

export function Select({ label, error, id, className = '', children, ...rest }: SelectProps) {
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <select
        id={id}
        className={`rounded-lg border px-3 py-2 text-sm text-gray-900 shadow-sm outline-none
          focus:border-primary-600 focus:ring-1 focus:ring-primary-700
          ${error ? 'border-red-400' : 'border-gray-300'}
          ${className}`}
        {...rest}
      >
        {children}
      </select>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
